import logger from '../utils/logger';
import { INVOICE_CONFIG, getReceiptSettings, getPrintSettings, calculateTax } from '../config/invoiceConfig';

class InvoiceService {
  constructor() {
    this.autoPrintEnabled = getPrintSettings().autoPrint;
    this.devMode = getPrintSettings().devMode;
  }

  printInvoice(order, baristaInfo = null, shopData = null) {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
  logger.warn('InvoiceService: Not in browser environment, skipping print');
      return;
    }

    try {
      // Create a small receipt-style window
      const windowSize = getPrintSettings().windowSize;
      const printWindow = window.open('', '_blank', `width=${windowSize.width},height=${windowSize.height},scrollbars=yes,resizable=yes`);
      
      if (!printWindow) {
  logger.warn('InvoiceService: Popup blocked, falling back to console preview');
        this.showConsolePreview(order, baristaInfo, shopData);
        return;
      }

      const invoiceContent = this.generateReceiptContent(order, baristaInfo, shopData);
      
      // Safely write to the new window
      printWindow.document.write(invoiceContent);
      printWindow.document.close();
      
      // Wait for content to load before attempting to print
      printWindow.onload = () => {
        if (this.autoPrintEnabled && !this.devMode) {
          setTimeout(() => {
            try {
              printWindow.print();
            } catch (printError) {
              logger.warn('InvoiceService: Auto-print failed, showing preview only:', printError);
            }
          }, 1000);
        }
      };

    } catch (error) {
  logger.error('InvoiceService: Error creating invoice window:', error);
      this.showConsolePreview(order, baristaInfo, shopData);
    }
  }

  generateReceiptContent(order, baristaInfo, shopData) {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const formattedTime = currentDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const cashierName = baristaInfo ? 
      `${baristaInfo.firstName || ''} ${baristaInfo.lastName || ''}`.trim() || 
      baristaInfo.email || 'Barista' : 'Barista';

    const subtotal = order.totalAmount;
    const tax = calculateTax(subtotal);
    const grandTotal = subtotal + tax;
    
    // Use dynamic shop data with fallbacks
    const cafeName = shopData?.name || 'COFFEE CLOUD';
    const cafeTagline = shopData?.description || 'Premium Coffee & Beverages';
    const cafeAddress = shopData?.address || '';
    const cafePhone = shopData?.phone || '';
    const cafeWebsite = shopData?.website || '';
    
    const receiptSettings = getReceiptSettings();

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - Table ${order.tableNumber}</title>
        <meta charset="utf-8">
        <style>
          @page {
            size: ${receiptSettings.width} 297mm;
            margin: 0;
          }
          
          body {
            font-family: ${receiptSettings.fontFamily};
            margin: 0;
            padding: 10px;
            background: white;
            color: black;
            font-size: ${receiptSettings.fontSize};
            line-height: 1.2;
            width: ${receiptSettings.width};
            max-width: ${receiptSettings.width};
          }
          
          .receipt {
            width: 100%;
            max-width: ${receiptSettings.width};
          }
          
          .header {
            text-align: center;
            margin-bottom: 10px;
            border-bottom: 1px dashed #000;
            padding-bottom: 10px;
          }
          
          .cafe-name {
            font-size: 16px;
            font-weight: bold;
            margin: 0;
            text-transform: uppercase;
          }
          
          .cafe-info {
            font-size: 10px;
            margin: 5px 0;
            color: #666;
          }
          
          .divider {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }
          
          .receipt-info {
            display: flex;
            justify-content: space-between;
            margin: 5px 0;
            font-size: 11px;
          }
          
          .items-table {
            width: 100%;
            margin: 10px 0;
            border-collapse: collapse;
          }
          
          .items-table th {
            text-align: left;
            font-size: 10px;
            padding: 2px 0;
            border-bottom: 1px solid #000;
          }
          
          .items-table td {
            padding: 2px 0;
            font-size: 11px;
          }
          
          .item-name {
            text-align: left;
            width: 50%;
          }
          
          .item-qty {
            text-align: center;
            width: 15%;
          }
          
          .item-total {
            text-align: right;
            width: 35%;
          }
          
          .totals {
            margin: 10px 0;
            border-top: 1px solid #000;
            padding-top: 5px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
            font-size: 11px;
          }
          
          .grand-total {
            font-weight: bold;
            font-size: 12px;
            border-top: 1px solid #000;
            padding-top: 5px;
            margin-top: 5px;
          }
          
          .payment-section {
            margin: 10px 0;
            border-top: 1px dashed #000;
            padding-top: 10px;
          }
          
          .payment-row {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
            font-size: 10px;
          }
          
          .footer {
            text-align: center;
            margin-top: 15px;
            font-size: 10px;
            color: #666;
            border-top: 1px dashed #000;
            padding-top: 10px;
          }
          
          .no-print {
            display: none;
          }
          
          @media print {
            body { 
              margin: 0; 
              padding: 5px;
            }
            .no-print { 
              display: none; 
            }
          }
          
          @media screen {
            .print-only {
              display: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1 class="cafe-name">${cafeName}</h1>
            ${cafeTagline ? `<div class="cafe-info">${cafeTagline}</div>` : ''}
            ${cafeAddress ? `<div class="cafe-info">${cafeAddress}</div>` : ''}
            ${cafePhone ? `<div class="cafe-info">Tel: ${cafePhone}</div>` : ''}
            ${cafeWebsite ? `<div class="cafe-info">${cafeWebsite}</div>` : ''}
          </div>
          
          <div class="divider"></div>
          
          <div class="receipt-info">
            <span>Receipt No.: #${order.id}</span>
            <span>Date: ${formattedDate}</span>
          </div>
          
          <div class="receipt-info">
            <span>Table No.: ${order.tableNumber}</span>
            <span>Time: ${formattedTime}</span>
          </div>
          
          <div class="receipt-info">
            <span>Cashier: ${cashierName}</span>
            <span>Type: ${order.origin === 'QR' ? 'QR' : 'Manual'}</span>
          </div>
          
          <div class="divider"></div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th class="item-name">Item Name</th>
                <th class="item-qty">Qty</th>
                <th class="item-total">Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map(item => `
                <tr>
                  <td class="item-name">${item.product.name}</td>
                  <td class="item-qty">${item.quantity}</td>
                  <td class="item-total">$${item.totalPrice.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="divider"></div>
          
          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            ${INVOICE_CONFIG.tax.enabled ? `
            <div class="total-row">
              <span>${INVOICE_CONFIG.tax.name}:</span>
              <span>$${tax.toFixed(2)}</span>
            </div>
            ` : ''}
            <div class="total-row grand-total">
              <span>Grand Total:</span>
              <span>$${grandTotal.toFixed(2)}</span>
            </div>
          </div>
          
          <div class="divider"></div>
          
          <div class="payment-section">
            <div class="payment-row">
              <span>Payment Detail:</span>
            </div>
            <div class="payment-row">
              <span>Mode</span>
              <span>Card Number</span>
              <span>Amount</span>
            </div>
            <div class="payment-row">
              <span>${INVOICE_CONFIG.payment.defaultMode}</span>
              <span>${INVOICE_CONFIG.payment.cardNumberMask}</span>
              <span>$${grandTotal.toFixed(2)}</span>
            </div>
          </div>
          
          <div class="divider"></div>
          
          <div class="footer">
            <p>Thank you for your visit ☕</p>
            <p>Order prepared at: ${formattedTime}</p>
            <p>Please come again!</p>
          </div>
        </div>
        
        <div class="no-print" style="margin-top: 20px; text-align: center;">
          <button onclick="window.print()" style="padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; margin-right: 8px;">
            Print Receipt
          </button>
          <button onclick="window.close()" style="padding: 8px 16px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;">
            Close
          </button>
        </div>
      </body>
      </html>
    `;
  }

  showConsolePreview(order, baristaInfo, shopData) {
    const cafeName = shopData?.name || 'COFFEE CLOUD';
  logger.info(`=== ${cafeName.toUpperCase()} RECEIPT ===`);
  logger.info(`Receipt No.: #${order.id}`);
  logger.info(`Table: ${order.tableNumber}`);
  logger.info(`Date: ${new Date().toLocaleString()}`);
  logger.info('---');
    order.items.forEach(item => {
  logger.info(`${item.product.name.padEnd(20)} ${item.quantity} x $${item.unitPrice.toFixed(2)} = $${item.totalPrice.toFixed(2)}`);
    });
  logger.info('---');
  logger.info(`Total: $${order.totalAmount.toFixed(2)}`);
  logger.info('=== END RECEIPT ===');
  }

  setAutoPrintEnabled(enabled) {
    this.autoPrintEnabled = enabled;
  }

  setDevMode(enabled) {
    this.devMode = enabled;
  }
}

const invoiceService = new InvoiceService();
export default invoiceService; 